import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Typography,
  Tooltip,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { ConversationApiClient } from "./api/ConversationApiClient";
import { DeepChatHistoryStorageManager } from "./api/DeepChatHistoryStorageManager";
import { PlannerChatHistoryStorageManager } from "./api/PlannerChatHistoryStorageManager";
import { ConversationHistoryMessageMapper } from "./api/ConversationHistoryMessageMapper";
import { useChat } from "./ChatProvider";

export function ChatHistoryToggle({ fullScreen = true }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [conversationsByDate, setConversationsByDate] = useState({});
  const [error, setError] = useState("");

  const {
    setChatMessages,
    conversationId,
    setConversationId,
    chatMode,
    deepHistoryId,
    setDeepHistoryId,
    plannerHistoryId,
    setPlannerHistoryId,
  } = useChat();

  const apiClient = React.useMemo(() => new ConversationApiClient(), []);
  const deepHistoryStorage = React.useMemo(
    () => new DeepChatHistoryStorageManager(),
    [],
  );
  const plannerHistoryStorage = React.useMemo(
    () => new PlannerChatHistoryStorageManager(),
    [],
  );
  const conversationHistoryMapper = React.useMemo(
    () => new ConversationHistoryMessageMapper(),
    [],
  );

  const formatGroupLabel = (raw) => {
    if (!raw) return "";
    if (raw === "today") return "Today";
    if (raw === "yesterday") return "Yesterday";
    if (raw === "last_7_days" || raw === "last7_days") {
      return "Last 7 days";
    }
    if (raw === "before_that") return "Older";
    return raw.replace(/_/g, " ");
  };

  const getRelativeGroup = (timestamp) => {
    if (!timestamp) return "before_that";
    const value = new Date(timestamp);
    if (Number.isNaN(value.getTime())) return "before_that";

    const now = new Date();
    const startOfToday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    );
    const startOfTarget = new Date(
      value.getFullYear(),
      value.getMonth(),
      value.getDate(),
    );
    const diffDays = Math.floor(
      (startOfToday.getTime() - startOfTarget.getTime()) /
        (24 * 60 * 60 * 1000),
    );

    if (diffDays === 0) return "today";
    if (diffDays === 1) return "yesterday";
    if (diffDays >= 2 && diffDays <= 7) return "last_7_days";
    return "before_that";
  };

  const getConversationId = (conversation) => {
    if (!conversation) return "";
    return conversation.conversation_id || conversation._id || "";
  };

  const getConversationTitle = (conversation) => {
    if (!conversation) return "Untitled conversation";
    if (conversation.title) return conversation.title;
    const id = getConversationId(conversation);
    return id ? `Conversation ${id.slice(-6)}` : "Untitled conversation";
  };

  const activeConversationId =
    chatMode === "deep"
      ? deepHistoryId
      : chatMode === "planner"
        ? plannerHistoryId
        : conversationId;

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const loadConversations = async () => {
    if (chatMode === "deep" || chatMode === "planner") {
      setLoading(false);
      setError("");
      const historyStorage =
        chatMode === "deep" ? deepHistoryStorage : plannerHistoryStorage;
      const sessions = historyStorage.listSessions();
      const grouped = sessions.reduce(
        (acc, session) => {
          const group = getRelativeGroup(
            session.updatedAt || session.createdAt,
          );
          if (!acc[group]) acc[group] = [];
          acc[group].push({
            _id: session.id,
            created_at: session.createdAt,
            updated_at: session.updatedAt,
            title: session.title,
          });
          return acc;
        },
        { today: [], yesterday: [], last_7_days: [], before_that: [] },
      );
      setConversationsByDate(grouped);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const grouped = await apiClient.list();
      setConversationsByDate(grouped || {});
    } catch (e) {
      setError("Unable to load conversations.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    loadConversations();
  }, [open, chatMode]);

  if (chatMode !== "normal" && chatMode !== "deep" && chatMode !== "planner") {
    return null;
  }

  const handleSelectConversation = async (id) => {
    if (!id) return;

    if (chatMode === "deep" || chatMode === "planner") {
      const historyStorage =
        chatMode === "deep" ? deepHistoryStorage : plannerHistoryStorage;
      const session = historyStorage.getSession(id);
      if (!session) {
        setError("Unable to load conversation.");
        return;
      }
      if (chatMode === "deep") {
        setDeepHistoryId(id);
      } else {
        setPlannerHistoryId(id);
      }
      setChatMessages(Array.isArray(session.messages) ? session.messages : []);
      setOpen(false);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const data = await apiClient.get(id);
      const messages = Array.isArray(data?.messages) ? data.messages : [];
      const mapped = conversationHistoryMapper.mapApiMessagesToChat(messages);

      setConversationId(id);
      setChatMessages(mapped);
      setOpen(false);
    } catch (e) {
      setError("Unable to load conversation.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConversation = async (event, id) => {
    event.stopPropagation();
    if (!id) return;
    if (chatMode === "deep" || chatMode === "planner") {
      const historyStorage =
        chatMode === "deep" ? deepHistoryStorage : plannerHistoryStorage;
      historyStorage.deleteSession(id);
      setConversationsByDate((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((date) => {
          next[date] = next[date].filter((c) => getConversationId(c) !== id);
          if (next[date].length === 0) {
            next[date] = [];
          }
        });
        return next;
      });
      if (chatMode === "deep" && deepHistoryId === id) {
        setDeepHistoryId(null);
        setChatMessages([]);
      }
      if (chatMode === "planner" && plannerHistoryId === id) {
        setPlannerHistoryId(null);
        setChatMessages([]);
      }
      return;
    }
    try {
      await apiClient.delete(id);
      setConversationsByDate((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((date) => {
          next[date] = next[date].filter((c) => getConversationId(c) !== id);
          if (next[date].length === 0) {
            delete next[date];
          }
        });
        return next;
      });
      if (conversationId === id) {
        setConversationId(null);
        setChatMessages([]);
      }
    } catch (e) {
      setError("Unable to delete conversation.");
    }
  };

  const handleCreateNewChat = () => {
    if (chatMode === "deep") {
      setDeepHistoryId(null);
    } else if (chatMode === "planner") {
      setPlannerHistoryId(null);
    } else {
      setConversationId(null);
    }
    setChatMessages([]);
    setOpen(false);
  };

  return (
    <>
      <Tooltip title="Show history">
        <IconButton
          onClick={handleOpen}
          aria-label="Open conversation history"
          sx={{
            mr: fullScreen ? 1 : 0,
            borderRadius: "10px",
            padding: 0.75,
            border: "1px solid rgba(24, 116, 210, 0.16)",
            backgroundColor: "#ffffff",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              backgroundColor: "rgba(24, 116, 210, 0.08)",
              transform: "translateY(-1px)",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
            },
          }}
        >
          <HistoryIcon
            sx={{
              color: "#1874d2",
              fontSize: { xs: 18, sm: 19, md: 20 },
            }}
          />
        </IconButton>
      </Tooltip>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 1.5,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, letterSpacing: "0.01em" }}
          >
            Conversation history
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="caption"
              sx={{ color: "#666666", minHeight: "1em" }}
            >
              {loading ? "Loading…" : ""}
            </Typography>
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateNewChat}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                boxShadow: "none",
              }}
            >
              New chat
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          <Box
            sx={{
              maxHeight: "60vh",
              overflowY: "auto",
            }}
          >
            {error && (
              <Typography variant="caption" sx={{ color: "#d32f2f", mb: 1 }}>
                {error}
              </Typography>
            )}

            {Object.keys(conversationsByDate).length === 0 && !loading ? (
              <Box
                sx={{
                  py: 4,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#777777",
                }}
              >
                <Typography variant="body2">No conversations yet.</Typography>
                <Typography variant="caption">
                  Start a chat to see it appear here.
                </Typography>
              </Box>
            ) : (
              <List
                dense
                sx={{
                  width: "100%",
                  bgcolor: "transparent",
                }}
              >
                {Object.entries(conversationsByDate).map(([date, items]) => (
                  <Box key={date} component="li" sx={{ p: 0 }}>
                    <ul style={{ padding: 0, margin: 0 }}>
                      <ListSubheader
                        disableSticky
                        sx={{
                          bgcolor: "transparent",
                          px: 0,
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          color: "#555",
                        }}
                      >
                        {formatGroupLabel(date)}
                      </ListSubheader>
                      {items.map((c) => (
                        <ListItemButton
                          key={getConversationId(c)}
                          onClick={() =>
                            handleSelectConversation(getConversationId(c))
                          }
                          selected={
                            activeConversationId === getConversationId(c)
                          }
                          sx={{
                            borderRadius: 1.5,
                            mb: 0.5,
                            px: 1.5,
                            py: 1,
                            "&.Mui-selected": {
                              backgroundColor: "rgba(24, 116, 210, 0.08)",
                            },
                            "&.Mui-selected:hover": {
                              backgroundColor: "rgba(24, 116, 210, 0.12)",
                            },
                          }}
                        >
                          <ListItemText
                            primary={getConversationTitle(c)}
                            secondary={
                              c.created_at
                                ? new Date(c.created_at).toLocaleTimeString()
                                : ""
                            }
                            primaryTypographyProps={{
                              noWrap: true,
                              fontSize: "0.85rem",
                              fontWeight: 500,
                            }}
                            secondaryTypographyProps={{
                              noWrap: true,
                              fontSize: "0.7rem",
                            }}
                            sx={{ mr: 1 }}
                          />
                          <IconButton
                            edge="end"
                            aria-label="Delete conversation"
                            size="small"
                            onClick={(e) =>
                              handleDeleteConversation(e, getConversationId(c))
                            }
                          >
                            <DeleteOutlineIcon
                              sx={{
                                fontSize: "1rem",
                                color: "#d32f2f",
                                "&:hover": {
                                  color: "#b71c1c",
                                },
                              }}
                            />
                          </IconButton>
                        </ListItemButton>
                      ))}
                    </ul>
                  </Box>
                ))}
              </List>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
